import { isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	inject,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID,
	SimpleChanges,
} from '@angular/core';
import { ViewportService } from '@services/geo/viewport.service';
import { SharedModule } from '@shared/modules/shared.module';

export interface TabHeader {
	labelKey?: string;
	label?: string;
	icon?: string;
	disabled?: boolean;
	/** Does not render the tab but keeps its index (e.g. clients in Configuration). */
	hidden?: boolean;
	/** Optional notification badge (count) rendered over the tab icon. Hidden when 0/undefined. */
	badge?: number;
}

/** Possible scroll containers of the PrimeNG tab strip (which one scrolls depends on type/viewport). */
const SCROLL_CANDIDATES = '.p-tablist-content, .p-tablist-viewport, .p-tablist, .p-tablist-tab-list';
/** Class set on the element that scrolls, so the scss can fade its edges. */
const SCROLLER_CLASS = 'tabs-scroller';

@Component({
	selector: 'app-tabs',
	imports: [SharedModule],
	templateUrl: './tabs.component.html',
	styleUrl: './tabs.component.scss',
	host: {
		'[class.tabs-type1]': 'type === "type1"',
		'[class.tabs-type2]': 'type === "type2"',
		'[class.tabs-type3]': 'type === "type3"',
		'[class.tabs-pill]': 'type === "pill"',
		'[class.tabs-large]': 'size === "large"',
		'[class.tabs-medium]': 'size === "medium"',
		'[class.tabs-small]': 'size === "small"',
		'[class.tabs-xsmall]': 'size === "xsmall"',
		'[class.tabs-fit-content]': 'fitContent',
		'[class.tabs-center-selected]': 'centerSelected && isMobile',
	},
})
export class TabsComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
	@Input() tabHeaders: TabHeader[] = [];
	@Input() type: 'type1' | 'type2' | 'type3' | 'pill' = 'type1';
	@Input() size: 'large' | 'medium' | 'small' | 'xsmall' = 'medium';
	@Input() fitContent = false; // Make tabs fit content width instead of equal distribution
	@Input() iconOnly = false; // Show only icons without labels
	@Input() centerSelected = false; // Center selected tab in viewport (for mobile scroll)

	@Input() selectedTab = 0;
	@Output() selectedTabChange = new EventEmitter<number>();

	isMobile = false;

	private scrollCandidates: HTMLElement[] = [];
	private resizeObserver?: ResizeObserver;
	private readonly onStripScroll = () => this.updateScrollFade();
	private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

	constructor(
		private elementRef: ElementRef,
		public viewportService: ViewportService,
	) {}

	ngOnInit(): void {
		this.updateMobileState();
	}

	@HostListener('window:resize')
	onResize(): void {
		this.updateMobileState();
		this.updateScrollFade();
	}

	private updateMobileState(): void {
		this.isMobile = this.viewportService.isMobileView();
	}

	get iconSize(): number {
		return this.size === 'xsmall' ? 16 : 24;
	}

	ngAfterViewInit(): void {
		if (this.centerSelected && this.isMobile) {
			// Small delay to ensure DOM is ready
			setTimeout(() => this.scrollToSelectedTab(false), 150);
		}
		// Wait for PrimeNG to render the strip before looking for its scroll container
		if (this.isBrowser) setTimeout(() => this.setupScrollFade());
	}

	ngOnDestroy(): void {
		for (const el of this.scrollCandidates) el.removeEventListener('scroll', this.onStripScroll);
		this.resizeObserver?.disconnect();
	}

	/**
	 * When the tab strip scrolls horizontally (mobile, hidden scrollbar) its edges fade out, so a
	 * half-visible last tab reads as "there is more" instead of a lone icon.
	 */
	private setupScrollFade(): void {
		const host = this.elementRef.nativeElement as HTMLElement;
		this.scrollCandidates = Array.from(host.querySelectorAll<HTMLElement>(SCROLL_CANDIDATES));
		for (const el of this.scrollCandidates) el.addEventListener('scroll', this.onStripScroll, { passive: true });
		if (typeof ResizeObserver !== 'undefined') {
			this.resizeObserver = new ResizeObserver(() => this.updateScrollFade());
			this.resizeObserver.observe(host);
		}
		this.updateScrollFade();
	}

	private updateScrollFade(): void {
		if (!this.isBrowser) return;
		const host = this.elementRef.nativeElement as HTMLElement;
		const scroller = this.scrollCandidates.find((el) => {
			const overflowX = getComputedStyle(el).overflowX;
			return (overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth + 1;
		});
		for (const el of this.scrollCandidates) el.classList.toggle(SCROLLER_CLASS, el === scroller);
		const fadeLeft = !!scroller && scroller.scrollLeft > 2;
		const fadeRight = !!scroller && scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 2;
		host.classList.toggle('tabs-fade-left', fadeLeft);
		host.classList.toggle('tabs-fade-right', fadeRight);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['selectedTab'] && !changes['selectedTab'].firstChange && this.centerSelected && this.isMobile) {
			setTimeout(() => this.scrollToSelectedTab(true), 50);
		}
	}

	selectTab(index: number): void {
		if (typeof index === 'number') {
			if (this.tabHeaders[index]?.disabled) return;
			this.selectedTab = index;
			this.selectedTabChange.emit(this.selectedTab);
			if (this.centerSelected && this.isMobile) {
				// Use requestAnimationFrame to ensure DOM has updated
				requestAnimationFrame(() => {
					this.scrollToSelectedTab(true);
				});
			}
		}
	}

	private scrollToSelectedTab(smooth = true): void {
		// Find the p-tablist element which is the scrollable container
		const pTablist = this.elementRef.nativeElement.querySelector('.p-tablist') as HTMLElement;
		if (!pTablist) return;

		// Get the tab list container
		const tabList = pTablist.querySelector('.p-tablist-tab-list') as HTMLElement;
		if (!tabList) return;

		// Get all tab elements
		const tabs = tabList.querySelectorAll('.p-tab');
		if (!tabs || tabs.length === 0) return;

		const selectedTabElement = tabs[this.selectedTab] as HTMLElement;
		if (!selectedTabElement) return;

		// The p-tablist is the scrollable element
		const scrollableElement = pTablist;

		// Get measurements
		const containerWidth = scrollableElement.clientWidth;
		const tabOffsetLeft = selectedTabElement.offsetLeft;
		const tabWidth = selectedTabElement.offsetWidth;

		// Calculate target scroll position to center the selected tab
		const tabCenter = tabOffsetLeft + tabWidth / 2;
		const targetScrollPosition = tabCenter - containerWidth / 2;

		// Clamp to valid scroll bounds
		const maxScroll = scrollableElement.scrollWidth - containerWidth;
		const clampedPosition = Math.max(0, Math.min(targetScrollPosition, maxScroll));

		scrollableElement.scrollTo({
			left: clampedPosition,
			behavior: smooth ? 'smooth' : 'auto',
		});
	}
}
